import React, { useState } from 'react';
import styled from 'styled-components';
import Hamburger from 'hamburger-react';
import type { SanityMenuItem } from '@repo/models/gatsby-graphql-types';
import type { GetInternalLinkFunctionType } from '@repo/models/GetInternalLinkFunctionType';
import getLink from '@repo/utils/getInternalLink';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import LinkComponent from '@repo/ui-components/LinkComponent';
import breakpoints from '@repo/ui-components/breakpoints';
import NavMobile from '@repo/ui-components/header/NavMobile';
import NavDesktop from '@repo/ui-components/header/NavDesktop';
import MegaMenu from '@repo/ui-components/header/MegaMenu';
import NavSearch from '@repo/ui-components/header/NavSearch';
import CartIcon from '@repo/ui-components/header/CartIcon';
import styles from '@repo/ui-components/styles';
import DealerSearchTrigger from '@repo/ui-components/dealer-search/DealerSearchTrigger';
import IconProfile from '@repo/ui-components/svgs/user-profile-icon.svg';

interface MinimalHeaderProps {
  currency?: string;
  brandName?: string;
  hideFromScreenReader?: boolean;
}

interface HeaderProps {
  menuStructure: SanityMenuItem[];
  searchIndexes?: string[];
  hasDealerSearch?: boolean;
  getInternalLink?: GetInternalLinkFunctionType;
  searchHitsPerPage?: number;
  currency?: string;
  brandName?: string;
  hideFromScreenReader?: boolean;
  showUtilityIcons?: boolean;
  /** Fired with the raw menu item whenever any of its links is clicked (desktop or mobile) — additive, defaults to a no-op. */
  onMenuItemClick?: (item: any) => void;
}

const Wrapper = styled.header`
  background: ${({ theme }) => theme.colors.headerBackground};
  display: flex;
  align-items: center;
  flex-direction: column;
  position: relative;
  z-index: 2;
`;

const Inner = styled.div<{ minimal?: boolean }>`
  background: inherit;
  display: flex;
  align-items: center;
  padding: ${styles.spacing['spacing-md']};
  max-width: ${breakpoints.xl};
  width: 100%;
  z-index: 2;
  ${props => props.minimal && 'justify-content: center;'}
`;

const MobileLogo = styled(LinkComponent)`
  display: flex;
  flex: 1;
  ${({ theme }) =>
    theme.images.mobileLogo
      ? `background-image: url(${theme.images.mobileLogo});`
      : `background-image: url(${theme.images.logo});`}
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  height: ${({ theme }) => theme.logoHeight};
  @media (min-width: ${breakpoints.lg}) {
    display: none;
  }
`;

const DesktopLogo = styled(LinkComponent)`
  display: none;
  background-image: url(${({ theme }) => theme.images.logo});
  background-repeat: no-repeat;
  background-position: left center;
  background-size: 200px 120px;
  height: 90px;
  @media (min-width: ${breakpoints.lg}) {
    display: flex;
    width: 200px;
  }
`;

const MobileNavTrigger = styled.div`
  width: 48px;
  height: 48px;

  @media (min-width: ${breakpoints.lg}) {
    display: none;
  }

  .hamburger-react div {
    background: ${({ theme }) =>
      theme?.colors?.hamburgerMenuColor || theme.colors.defaultFont} !important;
  }
`;

const UtilityList = styled.ul`
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  li {
    margin: 0;
    padding: 0;
  }
`;

const NavDesktopAccount = styled.li`
  display: none;
  @media (min-width: ${breakpoints.lg}) {
    display: flex;
  }
  svg {
    transition: all 0.5s ease;
    fill: ${({ theme }) => theme.colors.defaultFont};
  }
  &:hover {
    svg {
      fill: ${({ theme }) => theme.colors.defaultFont};
    }
  }
`;

const AccountLink = styled(LinkComponent)`
  display: flex;
  font-size: inherit;
  margin-top: 1rem;
  padding: 1.5rem 1rem 1.5rem 0.75rem;
  border-top: 1px solid;
  border-top-color: inherit;
  align-items: center;
  color: inherit;
  text-decoration: none;
  font-weight: inherit;

  svg {
    fill: ${({ theme }) => theme.colors.defaultFont};
  }

  @media (min-width: ${breakpoints.lg}) {
    border: none;
    margin-top: 0;
  }

  span {
    margin-top: -5px;
    padding-left: 0.5rem;
    @media (min-width: ${breakpoints.lg}) {
      display: none;
    }
  }
`;

const SearchContainer = styled.div`
  display: none;
  position: relative;
  width: 14rem;
  @media (min-width: ${breakpoints.lg}) {
    display: block;
  }

  @media (min-width: ${breakpoints.xl}) {
    width: 25rem;
  }
`;

const DealerSearchTriggerContainer = styled.div`
  background: ${({ theme }) => theme.colors.brandPrimary};
  width: 100%;
  @media (min-width: ${breakpoints.lg}) {
    display: none;
  }
`;

const MinimalHeader: React.FC<MinimalHeaderProps> = ({
  currency = 'USD',
  brandName = '',
  hideFromScreenReader = false,
}) => {
  const homepageUrl = getLink({ slug: '/', currency });
  const { tt } = useUiComponentsInternationalContext();

  const goToHomepageLabel = brandName
    ? tt(s => s.headerCommon.linkToBrandHomePageAriaLabel, { brandName })
    : tt(s => s.headerCommon.linkToHomePageAriaLabel);

  return (
    <Wrapper aria-hidden={hideFromScreenReader}>
      <Inner minimal>
        <MobileLogo
          to={homepageUrl}
          data-cy="mobile-logo"
          aria-label={goToHomepageLabel}
        />
        <DesktopLogo
          to={homepageUrl}
          data-cy="desktop-logo"
          aria-label={goToHomepageLabel}
        />
      </Inner>
    </Wrapper>
  );
};

const Header: React.FC<HeaderProps> = ({
  menuStructure,
  searchIndexes = [],
  hasDealerSearch = false,
  getInternalLink = () => '',
  searchHitsPerPage = 5,
  currency = 'USD',
  brandName = '',
  hideFromScreenReader = false,
  showUtilityIcons = true,
  onMenuItemClick = () => {},
}) => {
  const { tt } = useUiComponentsInternationalContext();
  const [mobileMenuActive, setMobileMenuActive] = useState(false);
  const [megaMenuActive, setMegaMenuActive] = useState<number | null>(null);
  const [keyboardNavActive, setKeyboardNavActive] = useState(false);

  const navLinks = menuStructure?.filter(section => section.menuType);

  const homepageUrl = getLink({ slug: '/', currency });

  const goToHomepageLabel = brandName
    ? tt(s => s.headerCommon.linkToBrandHomePageAriaLabel, { brandName })
    : tt(s => s.headerCommon.linkToHomePageAriaLabel);

  const getInternalLinkWithCurrency = props =>
    getInternalLink({
      ...props,
      currency,
    });

  return (
    <Wrapper aria-hidden={hideFromScreenReader}>
      <Inner>
        <MobileNavTrigger>
          <Hamburger
            size={24}
            toggled={mobileMenuActive}
            toggle={setMobileMenuActive}
            label={tt(s => s.headerCommon.mobileNavigationHamburgerLabel)}
          />
        </MobileNavTrigger>
        <MobileLogo
          to={homepageUrl}
          data-cy="mobile-logo"
          aria-label={goToHomepageLabel}
        />
        <DesktopLogo
          to={homepageUrl}
          data-cy="desktop-logo"
          aria-label={goToHomepageLabel}
        />
        <>
          <NavDesktop
            navLinks={navLinks}
            setMegaMenuActive={setMegaMenuActive}
            megaMenuActive={megaMenuActive}
            setKeyboardNavActive={setKeyboardNavActive}
            keyboardNavActive={keyboardNavActive}
            getInternalLink={getInternalLinkWithCurrency}
          />
          {navLinks?.map(
            (shopSection, id) =>
              shopSection.menuType === 'megaMenu' &&
              shopSection.items?.length > 0 && (
                <MegaMenu
                  key={`mega-${shopSection?.title}`}
                  megaMenuIndex={id}
                  megaMenuActive={megaMenuActive}
                  setMegaMenuActive={setMegaMenuActive}
                  keyboardNavActive={keyboardNavActive}
                  menuStructure={shopSection}
                  columns={shopSection.columns}
                  getInternalLink={getInternalLinkWithCurrency}
                  onItemClick={onMenuItemClick}
                />
              )
          )}

          <SearchContainer>
            <NavSearch
              searchIndexes={searchIndexes}
              hitsPerPage={searchHitsPerPage}
            />
          </SearchContainer>
        </>

        {showUtilityIcons && (
          <UtilityList>
            <NavDesktopAccount>
              <AccountLink
                to={getLink({ slug: '/account', currency })}
                title={tt(s => s.headerCommon.myAccountLink)}
                aria-label={tt(s => s.headerCommon.myAccountAriaLabel)}
              >
                <IconProfile width={21} height={21} />
              </AccountLink>
            </NavDesktopAccount>
            <li>
              <CartIcon />
            </li>
          </UtilityList>
        )}
      </Inner>
      <NavMobile
        mobileMenuActive={mobileMenuActive}
        navLinks={navLinks}
        accountLinkComponent={AccountLink}
        searchIndexes={searchIndexes}
        getInternalLink={getInternalLinkWithCurrency}
        searchHitsPerPage={searchHitsPerPage}
        showUtilityIcons={showUtilityIcons}
        onItemClick={onMenuItemClick}
      />

      {hasDealerSearch && (
        <DealerSearchTriggerContainer>
          <DealerSearchTrigger />
        </DealerSearchTriggerContainer>
      )}
    </Wrapper>
  );
};

export { MinimalHeader, Header };

export default Header;
